import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Building2,
  Users, ShoppingCart, TrendingDown, Handshake, DollarSign,
  ArrowUpRight, ArrowDownRight, RefreshCw, Settings, X, Search, Download, Link2, Copy, UserPlus
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const TODAY = new Date().toISOString().split("T")[0];
const MESES = [
  "Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];
const ANOS = Array.from({ length: 25 }, (_, i) => 2026 + i);

const formatCurrency = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

// ── Schemas ──
const clienteSchema = z.object({
  nome: z.string().min(1, "Nome obrigatorio"),
  valor: z.coerce.number().min(0),
  data: z.string().min(1, "Data obrigatoria"),
});
const revendaSchema = z.object({
  servidor: z.string().min(1, "Servidor obrigatorio"),
  data: z.string().min(1, "Data obrigatoria"),
  quantidade: z.coerce.number().min(0),
  valor_unitario: z.coerce.number().min(0),
  descricao: z.string().optional().default(""),
});
const investimentoSchema = z.object({
  banco: z.string().min(1, "Banco obrigatorio"),
  data: z.string().min(1, "Data obrigatoria"),
  quantidade: z.coerce.number().min(0),
  valor_unitario: z.coerce.number().min(0),
  descricao: z.string().optional().default(""),
});
const parceiroSchema = z.object({
  nome: z.string().min(1, "Nome obrigatorio"),
  valor: z.coerce.number().min(0),
  data: z.string().min(1, "Data obrigatoria"),
  descricao: z.string().optional().default(""),
});

export default function HubEmpresaPage() {
  const { authAxios } = useAuth();
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [ano, setAno] = useState(now.getFullYear());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [clientes, setClientes] = useState([]);
  const [revendas, setRevendas] = useState([]);
  const [investimentos, setInvestimentos] = useState([]);
  const [parceiros, setParceiros] = useState([]);

  const [dialog, setDialog] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bancos, setBancos] = useState([]);
  const [dConfigBancos, setDConfigBancos] = useState(false);
  const [novoBanco, setNovoBanco] = useState("");
  const [servidores, setServidores] = useState([]);
  const [dConfigServidores, setDConfigServidores] = useState(false);
  const [novoServidor, setNovoServidor] = useState("");
  const [buscaClientes, setBuscaClientes] = useState("");
  const [buscaRevendas, setBuscaRevendas] = useState("");
  const [buscaInvest, setBuscaInvest] = useState("");
  const [buscaParceiros, setBuscaParceiros] = useState("");
  const [funcionarios, setFuncionarios] = useState([]);
  const [dConfigFunc, setDConfigFunc] = useState(false);
  const [novoFunc, setNovoFunc] = useState("");

  const fCliente = useForm({ resolver: zodResolver(clienteSchema), defaultValues: { nome: "", valor: 0, data: TODAY } });
  const fRevenda = useForm({ resolver: zodResolver(revendaSchema), defaultValues: { servidor: "", data: TODAY, quantidade: 0, valor_unitario: 0, descricao: "" } });
  const fInvest = useForm({ resolver: zodResolver(investimentoSchema), defaultValues: { banco: "", data: TODAY, quantidade: 0, valor_unitario: 0, descricao: "" } });
  const fParceiro = useForm({ resolver: zodResolver(parceiroSchema), defaultValues: { nome: "", valor: 0, data: TODAY, descricao: "" } });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const p = `mes=${mes}&ano=${ano}`;
      const [c, r, i, pa, s, b, sv, fn] = await Promise.all([
        authAxios.get(`/hub-empresa/clientes?${p}`),
        authAxios.get(`/hub-empresa/revendas?${p}`),
        authAxios.get(`/hub-empresa/investimentos?${p}`),
        authAxios.get(`/hub-empresa/parceiros?${p}`),
        authAxios.get(`/hub-empresa/stats?${p}`),
        authAxios.get("/hub-empresa/config/bancos"),
        authAxios.get("/hub-empresa/config/servidores"),
        authAxios.get("/hub-empresa/funcionarios"),
      ]);
      setClientes(c.data); setRevendas(r.data);
      setInvestimentos(i.data); setParceiros(pa.data);
      setStats(s.data); setBancos(b.data); setServidores(sv.data);
      setFuncionarios(fn.data);
    } catch { toast.error("Erro ao carregar dados"); }
    finally { setLoading(false); }
  }, [authAxios, mes, ano]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const prevMes = () => { if (mes === 1) { setMes(12); setAno(ano - 1); } else setMes(mes - 1); };
  const nextMes = () => { if (mes === 12) { setMes(1); setAno(ano + 1); } else setMes(mes + 1); };

  // ── CRUD helpers ──
  const openAdd = (tipo, form) => { setEditItem(null); form.reset(); setDialog(tipo); };
  const openEdit = (tipo, form, item) => { setEditItem(item); form.reset(item); setDialog(tipo); };
  const handleDelete = async (tipo, id) => {
    if (!window.confirm("Excluir este registro?")) return;
    try { await authAxios.delete(`/hub-empresa/${tipo}/${id}`); toast.success("Removido!"); fetchAll(); }
    catch { toast.error("Erro ao remover"); }
  };

  const handleDeleteAll = async (tipo, label) => {
    const nomeMes = ["Janeiro","Fevereiro","Marco","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"][mes - 1];
    if (!window.confirm(`Tem certeza que deseja EXCLUIR TODOS os registros de "${label}" em ${nomeMes}/${ano}? Esta acao NAO pode ser desfeita.`)) return;
    try {
      const res = await authAxios.delete(`/hub-empresa/limpar-aba?tipo=${tipo}&mes=${mes}&ano=${ano}`);
      toast.success(`${res.data.removidos} registros removidos!`);
      fetchAll();
    } catch { toast.error("Erro ao excluir todos"); }
  };

  const handleExport = async (formato) => {
    try {
      toast.info("Gerando arquivo...");
      const res = await authAxios.get(`/hub-empresa/export/${formato}?mes=${mes}&ano=${ano}`, { responseType: "blob" });
      const ext = formato === "excel" ? "xlsx" : formato;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `hub_empresa_${mes}_${ano}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Arquivo baixado!");
    } catch { toast.error("Erro ao exportar"); }
  };

  const submitCliente = async (data) => {
    setSubmitting(true);
    try {
      if (editItem) { await authAxios.put(`/hub-empresa/clientes/${editItem.id}`, data); toast.success("Atualizado!"); }
      else { await authAxios.post("/hub-empresa/clientes", { ...data, mes, ano }); toast.success("Adicionado!"); }
      setDialog(null); fetchAll();
    } catch { toast.error("Erro ao salvar"); } finally { setSubmitting(false); }
  };
  const submitRevenda = async (data) => {
    setSubmitting(true);
    try {
      const payload = { ...data, valor_total: data.quantidade * data.valor_unitario, mes, ano };
      if (editItem) { await authAxios.put(`/hub-empresa/revendas/${editItem.id}`, payload); toast.success("Atualizado!"); }
      else { await authAxios.post("/hub-empresa/revendas", payload); toast.success("Adicionado!"); }
      setDialog(null); fetchAll();
    } catch { toast.error("Erro ao salvar"); } finally { setSubmitting(false); }
  };
  const submitInvestimento = async (data) => {
    setSubmitting(true);
    try {
      const payload = { ...data, valor_total: data.quantidade * data.valor_unitario, mes, ano };
      if (editItem) { await authAxios.put(`/hub-empresa/investimentos/${editItem.id}`, payload); toast.success("Atualizado!"); }
      else { await authAxios.post("/hub-empresa/investimentos", payload); toast.success("Adicionado!"); }
      setDialog(null); fetchAll();
    } catch { toast.error("Erro ao salvar"); } finally { setSubmitting(false); }
  };
  const submitParceiro = async (data) => {
    setSubmitting(true);
    try {
      if (editItem) { await authAxios.put(`/hub-empresa/parceiros/${editItem.id}`, data); toast.success("Atualizado!"); }
      else { await authAxios.post("/hub-empresa/parceiros", { ...data, mes, ano }); toast.success("Adicionado!"); }
      setDialog(null); fetchAll();
    } catch { toast.error("Erro ao salvar"); } finally { setSubmitting(false); }
  };

  // ── Render table helper ──
  const renderTable = (items, columns, tipo, form, searchValue, setSearchValue) => {
    const searchKey = columns[0]?.key || "";
    const filtered = searchValue
      ? items.filter((item) => {
          const val = String(item[searchKey] || "").toLowerCase();
          return val.includes(searchValue.toLowerCase());
        })
      : items;
    const labelAba = { clientes: "Clientes Pagos", revendas: "Revendas", investimentos: "Investimentos", parceiros: "Parceiros" }[tipo] || tipo;
    return (
      <>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Button size="sm" onClick={() => openAdd(tipo, form)} data-testid={`add-${tipo}-btn`}>
            <Plus className="w-4 h-4 mr-1" /> Adicionar
          </Button>
          <div className="flex items-center gap-1 max-w-xs">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={`Buscar por ${columns[0]?.label || ""}...`}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-9 h-9"
                data-testid={`search-${tipo}`}
              />
            </div>
            {searchValue && (
              <button className="shrink-0 p-1 rounded text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={() => setSearchValue("")}>
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          <div className="ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDeleteAll(tipo, labelAba)}
              disabled={items.length === 0}
              data-testid={`delete-all-${tipo}-btn`}
              className="text-red-500 hover:text-red-400 border-red-500/30 hover:bg-red-500/10 disabled:opacity-40"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Excluir Todos
            </Button>
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-8 text-sm">
            {searchValue ? "Nenhum resultado encontrado" : "Nenhum registro encontrado"}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
                    <TableHead className="w-[80px]">Acoes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((item) => (
                    <TableRow key={item.id}>
                      {columns.map((c) => (
                        <TableCell key={c.key}>{c.format ? c.format(item[c.key], item) : item[c.key]}</TableCell>
                      ))}
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(tipo, form, item)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(tipo, item.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className={`mt-3 p-3 rounded-lg flex justify-between items-center text-sm font-semibold ${tipo === "investimentos" || tipo === "parceiros" ? "bg-red-500/10 text-red-400" : "bg-emerald-500/10 text-emerald-400"}`}>
              <span>Total{searchValue ? " (filtrado)" : ""}:</span>
              <span>{formatCurrency(filtered.reduce((a, i) => a + (i.valor_total ?? i.valor ?? 0), 0))}</span>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="space-y-6" data-testid="hub-empresa-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-500/10">
            <Building2 className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Hub Empresa</h2>
            <p className="text-sm text-muted-foreground">Controle financeiro empresarial</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prevMes} data-testid="prev-month-btn"><ChevronLeft className="w-4 h-4" /></Button>
          <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
            <SelectTrigger className="w-[130px]" data-testid="month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MESES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
            <SelectTrigger className="w-[90px]" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{ANOS.map((a) => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={nextMes} data-testid="next-month-btn"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" onClick={fetchAll} data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-btn">
                <Download className="w-4 h-4 mr-1" /> Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport("pdf")}>PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("excel")}>Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("txt")}>Texto (.txt)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-emerald-500/20 bg-emerald-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><ArrowUpRight className="w-4 h-4 text-emerald-400" /><span className="text-xs text-muted-foreground">Entradas</span></div>
              <p className="text-lg font-bold text-emerald-400" data-testid="stat-entradas">{formatCurrency(stats.total_entradas)}</p>
            </CardContent>
          </Card>
          <Card className="border-red-500/20 bg-red-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><ArrowDownRight className="w-4 h-4 text-red-400" /><span className="text-xs text-muted-foreground">Saidas</span></div>
              <p className="text-lg font-bold text-red-400" data-testid="stat-saidas">{formatCurrency(stats.total_saidas)}</p>
            </CardContent>
          </Card>
          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><DollarSign className="w-4 h-4 text-blue-400" /><span className="text-xs text-muted-foreground">Saldo</span></div>
              <p className={`text-lg font-bold ${stats.saldo >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="stat-saldo">{formatCurrency(stats.saldo)}</p>
            </CardContent>
          </Card>
          <Card className="border-amber-500/20 bg-amber-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-amber-400" /><span className="text-xs text-muted-foreground">Clientes Pagos</span></div>
              <p className="text-lg font-bold text-amber-400" data-testid="stat-qtd">{stats.qtd_clientes}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <Card className="border-border/60">
          <CardContent className="p-4 pt-4">
            <Tabs defaultValue="clientes" className="w-full">
              <TabsList className="w-full grid grid-cols-4 mb-4" data-testid="empresa-tabs">
                <TabsTrigger value="clientes" className="gap-1.5" data-testid="tab-clientes">
                  <Users className="w-4 h-4" /> Clientes Pagos
                </TabsTrigger>
                <TabsTrigger value="revendas" className="gap-1.5" data-testid="tab-revendas">
                  <ShoppingCart className="w-4 h-4" /> Revendas
                </TabsTrigger>
                <TabsTrigger value="investimentos" className="gap-1.5" data-testid="tab-investimentos">
                  <TrendingDown className="w-4 h-4" /> Investimento
                </TabsTrigger>
                <TabsTrigger value="parceiros" className="gap-1.5" data-testid="tab-parceiros">
                  <Handshake className="w-4 h-4" /> Parceiros
                </TabsTrigger>
              </TabsList>

              {/* Tab: Clientes Pagos */}
              <TabsContent value="clientes">
                {renderTable(clientes, [
                  { key: "nome", label: "Nome" },
                  { key: "valor", label: "Valor", format: (v) => formatCurrency(v) },
                  { key: "data", label: "Data" },
                  { key: "funcionario", label: "Funcionario", format: (v) => v || "-" },
                ], "clientes", fCliente, buscaClientes, setBuscaClientes)}
              </TabsContent>

              {/* Tab: Revendas */}
              <TabsContent value="revendas">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center gap-1 max-w-xs">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Buscar por Servidor..." value={buscaRevendas} onChange={(e) => setBuscaRevendas(e.target.value)} className="pl-9 h-9" data-testid="search-revendas" />
                    </div>
                    {buscaRevendas && (
                      <button className="shrink-0 p-1 rounded text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={() => setBuscaRevendas("")}>
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setDConfigServidores(true)} data-testid="config-servidores-btn">
                    <Settings className="w-4 h-4 mr-1" /> Servidores
                  </Button>
                  <Button size="sm" onClick={() => openAdd("revendas", fRevenda)} data-testid="add-revendas-btn">
                    <Plus className="w-4 h-4 mr-1" /> Adicionar
                  </Button>
                  <div className="ml-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteAll("revendas", "Revendas")}
                      disabled={revendas.length === 0}
                      data-testid="delete-all-revendas-btn"
                      className="text-red-500 hover:text-red-400 border-red-500/30 hover:bg-red-500/10 disabled:opacity-40"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Excluir Todos
                    </Button>
                  </div>
                </div>
                {(() => {
                  const filtered = buscaRevendas ? revendas.filter(i => i.servidor?.toLowerCase().includes(buscaRevendas.toLowerCase())) : revendas;
                  return filtered.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8 text-sm">{buscaRevendas ? "Nenhum resultado encontrado" : "Nenhum registro encontrado"}</p>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Servidor</TableHead><TableHead>Data</TableHead><TableHead>Qtd</TableHead>
                              <TableHead>Unitario</TableHead><TableHead>Total</TableHead><TableHead>Descricao</TableHead><TableHead>Funcionario</TableHead>
                              <TableHead className="w-[80px]">Acoes</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filtered.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.servidor}</TableCell><TableCell>{item.data}</TableCell>
                                <TableCell>{item.quantidade}</TableCell><TableCell>{formatCurrency(item.valor_unitario)}</TableCell>
                                <TableCell>{formatCurrency(item.valor_total)}</TableCell><TableCell>{item.descricao}</TableCell><TableCell>{item.funcionario || "-"}</TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit("revendas", fRevenda, item)}><Pencil className="w-3.5 h-3.5" /></Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete("revendas", item.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 text-emerald-400 flex justify-between items-center text-sm font-semibold">
                        <span>Total{buscaRevendas ? " (filtrado)" : ""}:</span>
                        <span>{formatCurrency(filtered.reduce((a, i) => a + (i.valor_total ?? 0), 0))}</span>
                      </div>
                    </>
                  );
                })()}
              </TabsContent>

              {/* Tab: Investimento */}
              <TabsContent value="investimentos">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center gap-1 max-w-xs">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Buscar por Banco..." value={buscaInvest} onChange={(e) => setBuscaInvest(e.target.value)} className="pl-9 h-9" data-testid="search-investimentos" />
                    </div>
                    {buscaInvest && (
                      <button className="shrink-0 p-1 rounded text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={() => setBuscaInvest("")}>
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setDConfigBancos(true)} data-testid="config-bancos-btn">
                    <Settings className="w-4 h-4 mr-1" /> Bancos
                  </Button>
                  <Button size="sm" onClick={() => openAdd("investimentos", fInvest)} data-testid="add-investimentos-btn">
                    <Plus className="w-4 h-4 mr-1" /> Adicionar
                  </Button>
                  <div className="ml-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteAll("investimentos", "Investimentos")}
                      disabled={investimentos.length === 0}
                      data-testid="delete-all-investimentos-btn"
                      className="text-red-500 hover:text-red-400 border-red-500/30 hover:bg-red-500/10 disabled:opacity-40"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Excluir Todos
                    </Button>
                  </div>
                </div>
                {(() => {
                  const filtered = buscaInvest ? investimentos.filter(i => i.banco?.toLowerCase().includes(buscaInvest.toLowerCase())) : investimentos;
                  return filtered.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8 text-sm">{buscaInvest ? "Nenhum resultado encontrado" : "Nenhum registro encontrado"}</p>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Banco</TableHead><TableHead>Data</TableHead><TableHead>Qtd</TableHead>
                              <TableHead>Unitario</TableHead><TableHead>Total</TableHead><TableHead>Descricao</TableHead><TableHead>Funcionario</TableHead>
                              <TableHead className="w-[80px]">Acoes</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filtered.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>{item.banco}</TableCell><TableCell>{item.data}</TableCell>
                                <TableCell>{item.quantidade}</TableCell><TableCell>{formatCurrency(item.valor_unitario)}</TableCell>
                                <TableCell>{formatCurrency(item.valor_total)}</TableCell><TableCell>{item.descricao}</TableCell><TableCell>{item.funcionario || "-"}</TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit("investimentos", fInvest, item)}><Pencil className="w-3.5 h-3.5" /></Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete("investimentos", item.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      <div className="mt-3 p-3 rounded-lg bg-red-500/10 text-red-400 flex justify-between items-center text-sm font-semibold">
                        <span>Total{buscaInvest ? " (filtrado)" : ""}:</span>
                        <span>{formatCurrency(filtered.reduce((a, i) => a + (i.valor_total ?? 0), 0))}</span>
                      </div>
                    </>
                  );
                })()}
              </TabsContent>

              {/* Tab: Parceiros */}
              <TabsContent value="parceiros">
                {renderTable(parceiros, [
                  { key: "nome", label: "Nome" },
                  { key: "valor", label: "Valor", format: (v) => formatCurrency(v) },
                  { key: "data", label: "Data" },
                  { key: "descricao", label: "Descricao" },
                  { key: "funcionario", label: "Funcionario", format: (v) => v || "-" },
                ], "parceiros", fParceiro, buscaParceiros, setBuscaParceiros)}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* ── Dialogs ── */}


      {/* Cliente */}
      <Dialog open={dialog === "clientes"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-clientes">
          <DialogHeader><DialogTitle>{editItem ? "Editar" : "Adicionar"} Cliente Pago</DialogTitle></DialogHeader>
          <form onSubmit={fCliente.handleSubmit(submitCliente)} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input {...fCliente.register("nome")} placeholder="Nome do cliente" data-testid="input-cliente-nome" />
              {fCliente.formState.errors.nome && <p className="text-xs text-destructive">{fCliente.formState.errors.nome.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>Valor (R$)</Label>
              <Input type="number" step="0.01" {...fCliente.register("valor")} data-testid="input-cliente-valor" />
            </div>
            <div className="space-y-2">
              <Label>Data</Label>
              <Input type="date" {...fCliente.register("data")} data-testid="input-cliente-data" />
              {fCliente.formState.errors.data && <p className="text-xs text-destructive">{fCliente.formState.errors.data.message}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-clientes-btn">{submitting ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Revenda */}
      <Dialog open={dialog === "revendas"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-revendas">
          <DialogHeader><DialogTitle>{editItem ? "Editar" : "Adicionar"} Revenda</DialogTitle></DialogHeader>
          <form onSubmit={fRevenda.handleSubmit(submitRevenda)} className="space-y-4">
            <div className="space-y-2">
              <Label>Servidor</Label>
              <Select value={fRevenda.watch("servidor") || ""} onValueChange={(v) => fRevenda.setValue("servidor", v)}>
                <SelectTrigger data-testid="input-revenda-servidor">
                  <SelectValue placeholder="Selecione o servidor" />
                </SelectTrigger>
                <SelectContent>
                  {servidores.map((s) => (
                    <SelectItem key={s.id} value={s.nome}>{s.nome}</SelectItem>
                  ))}
                  {servidores.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum servidor. Cadastre em Configuracoes.</div>
                  )}
                </SelectContent>
              </Select>
              {fRevenda.formState.errors.servidor && <p className="text-xs text-destructive">{fRevenda.formState.errors.servidor.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>Data</Label>
              <Input type="date" {...fRevenda.register("data")} data-testid="input-revenda-data" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Quantidade / Creditos</Label>
                <Input type="number" step="0.01" {...fRevenda.register("quantidade")} data-testid="input-revenda-qtd" />
              </div>
              <div className="space-y-2">
                <Label>Valor Unitario</Label>
                <Input type="number" step="0.01" {...fRevenda.register("valor_unitario")} data-testid="input-revenda-unit" />
              </div>
            </div>
            <ValorTotalPreview form={fRevenda} color="emerald" />
            <div className="space-y-2">
              <Label>Descricao</Label>
              <Textarea {...fRevenda.register("descricao")} placeholder="Descricao (opcional)" data-testid="input-revenda-desc" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-revendas-btn">{submitting ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Investimento */}
      <Dialog open={dialog === "investimentos"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-investimentos">
          <DialogHeader><DialogTitle>{editItem ? "Editar" : "Adicionar"} Investimento</DialogTitle></DialogHeader>
          <form onSubmit={fInvest.handleSubmit(submitInvestimento)} className="space-y-4">
            <div className="space-y-2">
              <Label>Banco</Label>
              <Select value={fInvest.watch("banco") || ""} onValueChange={(v) => fInvest.setValue("banco", v)}>
                <SelectTrigger data-testid="input-invest-banco">
                  <SelectValue placeholder="Selecione o banco" />
                </SelectTrigger>
                <SelectContent>
                  {bancos.map((b) => (
                    <SelectItem key={b.id} value={b.nome}>{b.nome}</SelectItem>
                  ))}
                  {bancos.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">Nenhum banco. Cadastre em Configuracoes.</div>
                  )}
                </SelectContent>
              </Select>
              {fInvest.formState.errors.banco && <p className="text-xs text-destructive">{fInvest.formState.errors.banco.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>Data</Label>
              <Input type="date" {...fInvest.register("data")} data-testid="input-invest-data" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Quantidade / Creditos</Label>
                <Input type="number" step="0.01" {...fInvest.register("quantidade")} data-testid="input-invest-qtd" />
              </div>
              <div className="space-y-2">
                <Label>Valor Unitario</Label>
                <Input type="number" step="0.01" {...fInvest.register("valor_unitario")} data-testid="input-invest-unit" />
              </div>
            </div>
            <ValorTotalPreview form={fInvest} color="red" />
            <div className="space-y-2">
              <Label>Descricao</Label>
              <Textarea {...fInvest.register("descricao")} placeholder="Descricao (opcional)" data-testid="input-invest-desc" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-investimentos-btn">{submitting ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Parceiro */}
      <Dialog open={dialog === "parceiros"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-parceiros">
          <DialogHeader><DialogTitle>{editItem ? "Editar" : "Adicionar"} Parceiro / Afiliado</DialogTitle></DialogHeader>
          <form onSubmit={fParceiro.handleSubmit(submitParceiro)} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input {...fParceiro.register("nome")} placeholder="Nome do parceiro" data-testid="input-parceiro-nome" />
            </div>
            <div className="space-y-2">
              <Label>Valor (R$)</Label>
              <Input type="number" step="0.01" {...fParceiro.register("valor")} data-testid="input-parceiro-valor" />
            </div>
            <div className="space-y-2">
              <Label>Data</Label>
              <Input type="date" {...fParceiro.register("data")} data-testid="input-parceiro-data" />
            </div>
            <div className="space-y-2">
              <Label>Descricao</Label>
              <Textarea {...fParceiro.register("descricao")} placeholder="Descricao (opcional)" data-testid="input-parceiro-desc" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
              <Button type="submit" disabled={submitting} data-testid="submit-parceiros-btn">{submitting ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Config Bancos */}
      <Dialog open={dConfigBancos} onOpenChange={setDConfigBancos}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-config-bancos">
          <DialogHeader><DialogTitle>Configurar Bancos (Investimento)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!novoBanco.trim()) return;
              try {
                await authAxios.post("/hub-empresa/config/bancos", { nome: novoBanco.trim() });
                toast.success("Banco criado!");
                setNovoBanco("");
                const res = await authAxios.get("/hub-empresa/config/bancos");
                setBancos(res.data);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Erro ao criar banco");
              }
            }} className="flex gap-2">
              <Input value={novoBanco} onChange={(e) => setNovoBanco(e.target.value)} placeholder="Nome do banco..." className="flex-1" data-testid="input-novo-banco" />
              <Button type="submit" size="sm" data-testid="add-banco-btn"><Plus className="w-4 h-4" /></Button>
            </form>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {bancos.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum banco cadastrado</p>
              ) : (
                bancos.map((b) => (
                  <div key={b.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                    <span className="text-sm font-medium">{b.nome}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={async () => {
                      if (!window.confirm("Excluir este banco?")) return;
                      try {
                        await authAxios.delete(`/hub-empresa/config/bancos/${b.id}`);
                        toast.success("Banco removido!");
                        const res = await authAxios.get("/hub-empresa/config/bancos");
                        setBancos(res.data);
                      } catch { toast.error("Erro ao remover"); }
                    }} data-testid={`delete-banco-${b.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Config Servidores */}
      <Dialog open={dConfigServidores} onOpenChange={setDConfigServidores}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-config-servidores">
          <DialogHeader><DialogTitle>Configurar Servidores (Revendas)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!novoServidor.trim()) return;
              try {
                await authAxios.post("/hub-empresa/config/servidores", { nome: novoServidor.trim() });
                toast.success("Servidor criado!");
                setNovoServidor("");
                const res = await authAxios.get("/hub-empresa/config/servidores");
                setServidores(res.data);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Erro ao criar servidor");
              }
            }} className="flex gap-2">
              <Input value={novoServidor} onChange={(e) => setNovoServidor(e.target.value)} placeholder="Nome do servidor..." className="flex-1" data-testid="input-novo-servidor" />
              <Button type="submit" size="sm" data-testid="add-servidor-btn"><Plus className="w-4 h-4" /></Button>
            </form>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {servidores.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum servidor cadastrado</p>
              ) : (
                servidores.map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                    <span className="text-sm font-medium">{s.nome}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={async () => {
                      if (!window.confirm("Excluir este servidor?")) return;
                      try {
                        await authAxios.delete(`/hub-empresa/config/servidores/${s.id}`);
                        toast.success("Servidor removido!");
                        const res = await authAxios.get("/hub-empresa/config/servidores");
                        setServidores(res.data);
                      } catch { toast.error("Erro ao remover"); }
                    }} data-testid={`delete-servidor-${s.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Config Funcionarios */}
      <Dialog open={dConfigFunc} onOpenChange={setDConfigFunc}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-config-func">
          <DialogHeader><DialogTitle>Gerenciar Funcionarios</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!novoFunc.trim()) return;
              try {
                await authAxios.post("/hub-empresa/funcionarios", { nome: novoFunc.trim() });
                toast.success("Funcionario criado!");
                setNovoFunc("");
                const res = await authAxios.get("/hub-empresa/funcionarios");
                setFuncionarios(res.data);
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Erro ao criar");
              }
            }} className="flex gap-2">
              <Input value={novoFunc} onChange={(e) => setNovoFunc(e.target.value)} placeholder="Nome do funcionario..." className="flex-1" data-testid="input-novo-func" />
              <Button type="submit" size="sm" data-testid="add-func-btn"><Plus className="w-4 h-4" /></Button>
            </form>
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {funcionarios.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhum funcionario cadastrado</p>
              ) : (
                funcionarios.map((f) => (
                  <div key={f.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{f.nome}</p>
                      <p className="text-xs text-muted-foreground truncate">{`${window.location.origin}/hub-empresa-func/${f.token}`}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500" onClick={async () => {
                        const url = `${window.location.origin}/hub-empresa-func/${f.token}`;
                        try { await navigator.clipboard.writeText(url); toast.success("Link copiado!"); }
                        catch { toast.info(url); }
                      }} title="Copiar link"><Copy className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={async () => {
                        if (!window.confirm(`Excluir funcionario ${f.nome}?`)) return;
                        try {
                          await authAxios.delete(`/hub-empresa/funcionarios/${f.id}`);
                          toast.success("Funcionario removido!");
                          const res = await authAxios.get("/hub-empresa/funcionarios");
                          setFuncionarios(res.data);
                        } catch { toast.error("Erro ao remover"); }
                      }}><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Small helper for valor_total preview
function ValorTotalPreview({ form, color }) {
  const qty = form.watch("quantidade") || 0;
  const unit = form.watch("valor_unitario") || 0;
  return (
    <div className={`p-3 rounded-lg bg-${color}-500/10 text-${color}-400 font-semibold text-center`}>
      Valor Total: {formatCurrency(qty * unit)}
    </div>
  );
}
